import { regex } from 'arkregex';
import {
  COLLAPSED_WHITESPACE_PATTERN,
  COMBINING_DIACRITIC_PATTERN,
  EMOJI_BLOCK_PATTERN,
  EMOJI_VARIATION_SELECTOR_PATTERN,
  LETTER_PATTERN,
  LOCATION_COMMA_SPACE_PATTERN,
  LOCATION_CONNECTOR_WORD_PATTERN,
  LOCATION_COORDINATE_CAPTURE_PATTERN,
  LOCATION_DASH_OR_SLASH_PATTERN,
  LOCATION_DUPLICATE_COMMA_PATTERN,
  LOCATION_NON_ALPHANUMERIC_PATTERN,
  LOCATION_QUOTE_PATTERN,
  LOCATION_SYMBOL_SEPARATOR_PATTERN,
  LOCATION_TRIM_DELIMITER_PATTERN,
} from '@/lib/utils/regex';

/**
 * Normalize a free-form location string to a deterministic cache key.
 *
 * Rules:
 * - Trim and lowercase
 * - Normalize unicode (NFKD) and remove diacritics
 * - Remove emoji and pictographs
 * - Collapse whitespace and common separators
 * - Remove surrounding punctuation and repeated commas
 * - Drop blatantly unhelpful placeholders (e.g., 'earth', 'world', 'everywhere')
 */
function parseCoordinatesFromString(s: string): { lat: number; lng: number } | null {
  // Find two floats separated by comma, optionally inside parentheses
  const m = s.match(LOCATION_COORDINATE_CAPTURE_PATTERN);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  const coordinatesAreFinite = Number.isFinite(lat) && Number.isFinite(lng);
  if (!coordinatesAreFinite) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;
  return { lat, lng };
}

const EXACT_DENYLIST = new Set<string>([
  '300 cities in 100 countries',
  'across state lines',
  'action over perfection',
  'all over the universe',
  'alpha quadrant',
  'an airbnb somewhere',
  'any cloud,k8s,edge',
  'apple ecosystem',
  'apple nerds booster',
  'applications,xcodeapp',
  'asilo arkham',
  'a tiny swift,js,kotlin,c package',
  'at my desk',
  'at this prvt',
  'best friend only',
  'between light,dark modes',
  'blog',
  'blog posts',
  'building,nomading',
  'chartstravaganza',
  'coding y startups',
  'contactus for promo of yourapp',
  'creating websites,apps',
  'cyber',
  'cyperpunkdistricts',
  'da84ceb213f1156605ab2b3a3f0363',
  'dev,null',
  'dev,tty',
  'dm for credit',
  'documenting the process at',
  'earth planet,mars soon',
  'earths surface',
  'en el mundo de mi dios y ddgp',
  'en theorie',
  'everywhere,all at once',
  'everywhere and anywhere',
  'fine film retailers everywhere',
  'fintwit',
  'fromtherivertothesea',
  'fully remote',
  'halftes6bsky',
  'hyperamerica',
  'hypervisor',
  'hypervm',
  'idfk',
  'im every where',
  'in a hilbert space',
  'in a slurm cluster',
  'in front of a mac',
  'in front of my laptop24,7',
  'inside your app',
  'in the notes',
  'in your framebuffer',
  'in your mobile device',
  'ios,ipados',
  'i will bring tiktok back',
  'jobbar hemma',
  'looking at the data',
  'mac app store,setapp',
  'made on earth by humans',
  'microplastics in the dryer',
  'mixed reality',
  'my full portfolio',
  'my permanent address is the future',
  'neither here nor there',
  'new jessicaville',
  'next,my neighbors',
  'niggaverse',
  'nonyah',
  'omnipresent',
  'omniverse',
  'parody not investment advice',
  'personal profile,views mine',
  'photo,photo0164jpg',
  'play store,app store and web',
  'political news dni',
  'right here,right now',
  'saturday planet',
  'space',
  'spacebaby parking lease676935',
  'startnodebot',
  'status linearstatuscom',
  'sting',
  'stock ios',
  'stuck in vim',
  'switfy land',
  'thankyouverygood',
  'the androids dungeon',
  'the blockchain',
  'the dreadlands',
  'the farthest reaches',
  'the interwebs',
  'the milky way galaxy',
  'the world wide web',
  'the zone',
  'the world without borders',
  'use current location',
  'usink computa',
  'views mine',
  'wadda ya wanna know',
  'wanna learn flutter',
  'where style is not a matter',
  'where the wind blows',
  'world wide web',
  'xcode',
  'xcode,betaapp',
  'xnu kernel',
  'xpatspace',
  'your devices',
  'your editor',
  'your friendly podcasting app',
  'your most cherished memories',
  'your screen',
]);

const DROP_WORDS = new Set<string>([
  'a',
  'aka',
  'airbnb',
  'an',
  'and',
  'any',
  'anytime',
  'anyway',
  'anywhere',
  'around',
  'at',
  'atm',
  'before',
  'behind',
  'betaapp',
  'beside',
  'besides',
  'between',
  'blows',
  'boat',
  'boats',
  'building',
  'but',
  'by',
  'c',
  'cherished',
  'coding',
  'contact',
  'contactus',
  'creating',
  'credit',
  'current',
  'desk',
  'device',
  'devices',
  'digital',
  'documenting',
  'during',
  'ecosystem',
  'everywhere',
  'fintwit',
  'framebuffer',
  'friendly',
  'future',
  'global',
  'he',
  'her',
  'here',
  'hers',
  'herself',
  'him',
  'himself',
  'his',
  'hopefully',
  'hyperamerica',
  'hypervisor',
  'hypervm',
  'i',
  'idfk',
  'idk',
  'im',
  'inside',
  'is',
  'js',
  'k8s',
  'kotlin',
  'learn',
  'laptop',
  'laptops',
  'laptop24',
  'linearstatuscom',
  'lovely',
  'matter',
  'maybe',
  'me',
  'memories',
  'microplastics',
  'mine',
  'mostly',
  'most',
  'my',
  'myself',
  'nearby',
  'nerds',
  'news',
  'newsletter',
  'newsletters',
  'nomad',
  'nomading',
  'none',
  'nowhere',
  'o',
  'of',
  'or',
  'other',
  'others',
  'our',
  'ours',
  'ourselves',
  'package',
  'packages',
  'perfection',
  'perseverance',
  'personal',
  'playstore',
  'podcast',
  'podcasting',
  'political',
  'portfolio',
  'process',
  'promo',
  'promos',
  'prvt',
  'rainy',
  'reality',
  'remote',
  'safe',
  'screen',
  'screens',
  'setapp',
  'she',
  'ship',
  'ships',
  'silent',
  'space',
  'spacebaby',
  'somewhere',
  'startnodebot',
  'status',
  'style',
  'substack',
  'subscribe',
  'thankyouverygood',
  'that',
  'the',
  'their',
  'them',
  'themselves',
  'there',
  'thereabout',
  'thereabouts',
  'they',
  'third',
  'this',
  'those',
  'wind',
  'first',
  'second',
  'universe',
  'unlearning',
  'use',
  'views',
  'wanna',
  'we',
  'where',
  'why',
  'worldwide',
  'xcode',
  'xnu',
  'xpatspace',
  'your',
  'yours',
  'yourself',
  'appstore',
  'dni',
]);

const THROWAWAY_PARTS = new Set<string>([
  'global',
  'he',
  'her',
  'hers',
  'him',
  'she',
  'they',
  'them',
  'xe',
  'xem',
  'ze',
  'zir',
  'fae',
  'faer',
  'ey',
  'em',
  'places',
  'pronouns',
]);

function stripJunkWords(part: string): string {
  if (!part) return '';
  const words = part
    .split(' ')
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  if (words.length === 0) {
    return '';
  }
  const filtered = words.filter((word) => !DROP_WORDS.has(word));
  if (filtered.length === 0) {
    return '';
  }
  return filtered.join(' ').replaceAll(COLLAPSED_WHITESPACE_PATTERN, ' ').trim();
}

const BLACKLIST_PATTERNS: RegExp[] = [
  regex.as('https?://', 'i'),
  regex.as('@', 'i'), // emails or handles
  regex.as(
    String.raw`\b(join|newsletter|subscribe|get (the|my)|readers|headshot|interviews|book|newsletter|follow me)\b`,
    'i'
  ),
  regex.as(String.raw`[\u2190-\u21FF\u27A1\u279C\u27B0\u27A4\u27A1\u25B6\u25C0]|\u2192|\u27A1|\u2B95|\u27A4|→|➡️`, 'u'),
  regex.as(String.raw`\$\s?\d`, 'i'), // money
  regex.as('mrr', 'i'),
  regex.as(String.raw`\b(book of bangers|goods\s*:|my book)\b`, 'i'),
];

const EXTRA_BLACKLIST_PATTERNS: RegExp[] = [
  regex.as(String.raw`\bright behind you\b`, 'i'),
  regex.as(String.raw`\bin my (own )?head\b`, 'i'),
  regex.as(String.raw`\bon my computer\b`, 'i'),
  regex.as(String.raw`\bcheck(out)? my (youtube|bio|link|newsletter)\b`, 'i'),
  regex.as(String.raw`press\[at\]`, 'i'),
  regex.as(String.raw`\b(click this|boob world|pretty good thanks)\b`, 'i'),
  regex.as(String.raw`\bhave ai do your tasks\b`, 'i'),
  regex.as(String.raw`\baws cost explorer\b`, 'i'),
  regex.as(String.raw`\byear of superagency\b`, 'i'),
  regex.as(String.raw`\bex inv banking\b`, 'i'),
  regex.as(String.raw`\bhallmark channel movie\b`, 'i'),
  regex.as(String.raw`\bdreamcast heaven\b`, 'i'),
  regex.as(String.raw`\btechnicolou?r dreams\b`, 'i'),
  regex.as(String.raw`\bcontent page.*promo\b`, 'i'),
  regex.as(String.raw`\bvilla straylight\b`, 'i'),
  regex.as(String.raw`\bsponsorships?\b`, 'i'),
  regex.as(String.raw`\bexperience the huddle\b`, 'i'),
  regex.as(String.raw`\bwifi pirate\b`, 'i'),
  regex.as(String.raw`\bsomewhere in protocol\b`, 'i'),
  regex.as(String.raw`\bthe goods\b`, 'i'),
  regex.as(String.raw`\bvibeland\b`, 'i'),
  regex.as(String.raw`\btry fenn\b`, 'i'),
  regex.as(String.raw`\bpressat\b`, 'i'),
  regex.as(String.raw`\bclaude,?code\b`, 'i'),
  regex.as(String.raw`\bbluesky\b`, 'i'),
];

export function normalizeLocationKey(input: string): string {
  let s = String(input || '')
    .trim()
    .toLowerCase();
  if (!s) return '';

  // Unicode normalize & remove diacritics
  s = s.normalize('NFKD').replaceAll(COMBINING_DIACRITIC_PATTERN, '');

  // Remove emojis/pictographs/symbols broadly
  // Ranges cover emoticons, pictographs, transport, flags, misc symbols
  s = s.replaceAll(EMOJI_BLOCK_PATTERN, '');

  // Remove emoji variation selectors left after emoji stripping
  s = s.replaceAll(EMOJI_VARIATION_SELECTOR_PATTERN, '');

  // Replace multiple separators with a single comma
  s = s.replaceAll(LOCATION_SYMBOL_SEPARATOR_PATTERN, ',');
  // Normalize common separators and connectors to comma
  s = s.replaceAll(LOCATION_CONNECTOR_WORD_PATTERN, ',');
  s = s.replaceAll(LOCATION_DASH_OR_SLASH_PATTERN, ',');

  // Remove stray quotes/backticks
  s = s.replaceAll(LOCATION_QUOTE_PATTERN, '');

  // Collapse whitespace
  s = s.replaceAll(COLLAPSED_WHITESPACE_PATTERN, ' ');

  // Normalize comma+space, remove duplicate commas
  s = s.replaceAll(LOCATION_COMMA_SPACE_PATTERN, ',');
  s = s.replaceAll(LOCATION_DUPLICATE_COMMA_PATTERN, ',');

  // Remove any remaining characters outside of [a-z0-9, ]
  s = s.replaceAll(LOCATION_NON_ALPHANUMERIC_PATTERN, '');

  // Trim commas and spaces
  s = s.replaceAll(LOCATION_TRIM_DELIMITER_PATTERN, '');

  // Drop generic placeholders and obvious non-locations
  const GENERIC = new Set([
    'earth',
    'world',
    'milky way',
    'everywhere',
    'somewhere',
    'internet',
    'undefined',
    'null',
    'none',
    'n/a',
    'na',
  ]);
  if (GENERIC.has(s)) return '';

  if (EXACT_DENYLIST.has(s)) return '';

  // Blacklist obvious non-location promos/emails/links
  // Additional phrase-level filters for common jokey or promo strings that definitely
  // are not locations.
  for (const re of [...BLACKLIST_PATTERNS, ...EXTRA_BLACKLIST_PATTERNS]) {
    if (re.test(input) || re.test(s)) return '';
  }

  // Remove generic trailing tokens from comma-separated lists
  const normalizedParts = s
    .split(',')
    .map((part) => stripJunkWords(part.trim()))
    .filter((part) => part.length > 0 && !THROWAWAY_PARTS.has(part));

  if (normalizedParts.length === 0) {
    return '';
  }

  s = normalizedParts.join(',');

  if (!s) {
    return '';
  }

  if (EXACT_DENYLIST.has(s)) {
    return '';
  }

  // Coordinates override
  const coords = parseCoordinatesFromString(input) || parseCoordinatesFromString(s);
  if (coords) {
    const lat = Number(coords.lat.toFixed(6));
    const lng = Number(coords.lng.toFixed(6));
    return `coords:${lat},${lng}`;
  }

  return s;
}

/** Basic guard to check if a location string is likely meaningful after normalization */
export function isMeaningfulLocation(input: string): boolean {
  const key = normalizeLocationKey(input);
  if (!key) return false;
  // Accept coordinate keys
  if (key.startsWith('coords:')) return true;
  // Require letters and reasonable length
  if (!LETTER_PATTERN.test(key)) return false;
  if (key.length < 2 || key.length > 100) return false;
  return true;
}
